import React, { useMemo } from "react";
import { Tooltip } from "primereact/tooltip";
import { Skeleton } from "primereact/skeleton";
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { userResult } from "../../models/orders/order-list-model";

// ==================== Skeleton Component ====================
export const PerformanceCardSkeleton = React.memo(() => (
  <div className="card-wrapper">
    <div className="skeleton-card">
      <Skeleton width="80%" height="1.5rem" className="mb-3" borderRadius="8px" />
      <Skeleton width="60%" height="3rem" className="mb-4" borderRadius="8px" />
      <Skeleton width="100%" height="1rem" className="mb-2" borderRadius="8px" />
      <Skeleton width="90%" height="1rem" borderRadius="8px" />
    </div>
  </div>
));

PerformanceCardSkeleton.displayName = "PerformanceCardSkeleton";

// ==================== Performance Card Component ====================
interface PerformanceCardProps {
  title: string;
  value: string | number;
  gradientClass: string;
  icon: string;
  tooltipId: string;
  tooltipContent: React.ReactNode;
  children?: React.ReactNode;
  index: number;
}

export const PerformanceCard = React.memo<PerformanceCardProps>(
  ({ title, value, gradientClass, icon, tooltipId, tooltipContent, children, index }) => {
    const progressValue = useMemo(() => {
      if (typeof value === "string" && value.includes("%")) {
        return parseFloat(value);
      }
      return 0;
    }, [value]);

    return (
      <div className="card-wrapper">
        <div className="glass-card">
          <div className={`card-accent-bar ${gradientClass}`} />

          <div className="card-header">
            <div className="card-header-content">
              <div className={`card-icon ${gradientClass}`}>
                <i className={`pi ${icon} text-white text-xl`}></i>
              </div>
              <h5 className="card-title">{title}</h5>
            </div>
            <i
              className="pi pi-info-circle card-info-icon"
              data-pr-tooltip-id={tooltipId}
            />
          </div>

          <div className="card-value-section">
            <p className="metric-number">{value ?? "--"}</p>

            {progressValue > 0 && (
              <div className="progress-bar-custom">
                <div
                  className={`progress-bar-fill ${gradientClass}`}
                  style={{ width: `${progressValue}%` }}
                />
              </div>
            )}
          </div>

          {children && (
            <>
              <div className="card-divider" />
              {children}
            </>
          )}

          <Tooltip target={`[data-pr-tooltip-id="${tooltipId}"]`} position="top">
            <div className="p-3" style={{ maxWidth: "300px" }}>
              {tooltipContent}
            </div>
          </Tooltip>
        </div>
      </div>
    );
  }
);

PerformanceCard.displayName = "PerformanceCard";

// ==================== Dashboard Header Component ====================
export const DashboardHeader: React.FC = () => (
  <div className="dashboard-header">
    <div className="flex align-items-center gap-3 mb-3">
      <div className="header-icon-wrapper">
        <i className="pi pi-chart-bar text-white text-4xl"></i>
      </div>
      <div>
        <h1 className="header-title">Driver Performance Dashboard</h1>
        <p className="header-subtitle">
          Real-time insights into delivery efficiency, cash settlement, and time performance
        </p>
      </div>
    </div>
  </div>
);

// ==================== Filter Component ====================
interface FiltersSectionProps {
  orderUser: userResult[];
  dropdownValue: number | null;
  selectedMonth: Date;
  onDropdownChange: (e: DropdownChangeEvent) => void;
  onDropdownFilter: (e: DropdownFilterEvent) => void;
  onMonthChange: (e: any) => void;
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({
  orderUser,
  dropdownValue,
  selectedMonth,
  onDropdownChange,
  onDropdownFilter,
  onMonthChange,
}) => (
  <div className="filter-container">
    <div className="filter-controls">
      <div className="filter-field">
        <label className="filter-label">
          <i className="pi pi-user filter-label-icon"></i>
          Select Driver
        </label>
        <Dropdown
          value={dropdownValue}
          options={orderUser}
          optionLabel="detail"
          optionValue="userID"
          onChange={onDropdownChange}
          onFilter={onDropdownFilter}
          filter
          placeholder="🔍 Search Driver Name/Mobile"
          className="w-full filter-input"
          showClear
        />
      </div>
      <div className="filter-field">
        <label className="filter-label">
          <i className="pi pi-calendar filter-label-icon"></i>
          Select Month
        </label>
        <Calendar
          value={selectedMonth}
          onChange={onMonthChange}
          view="month"
          dateFormat="M yy"
          showIcon
          className="w-full"
          inputClassName="filter-input"
        />
      </div>
    </div>
  </div>
);

// ==================== Empty State Component ====================
interface EmptyStateProps {
  icon: string;
  gradientClass: string;
  title: string;
  description: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  gradientClass,
  title,
  description,
}) => (
  <div className="empty-state-wrapper">
    <div className="glass-card empty-state-card">
      <div className={`empty-state-icon-wrapper ${gradientClass}`}>
        <i className={`pi ${icon} text-white text-4xl`}></i>
      </div>
      <h3 className="empty-state-title">{title}</h3>
      <p className="empty-state-text" dangerouslySetInnerHTML={{ __html: description }} />
    </div>
  </div>
);

// ==================== Tooltip Content ====================
export const tooltipContents = {
  delivery: (
    <div>
      <p className="font-bold mb-2 text-base">📦 Delivery Completion</p>
      <p className="text-sm mb-2">
        Measures the percentage of successfully delivered orders out of total assigned orders.
      </p>
      <ul className="list-disc pl-4 text-sm space-y-1">
        <li>Formula: (Delivered ÷ Assigned) × 100</li>
        <li>Higher percentage indicates better reliability</li>
        <li>Benchmark: 90%+ is excellent</li>
      </ul>
    </div>
  ),
  cash: (
    <div>
      <p className="font-bold mb-2 text-base">💰 Cash Settlement</p>
      <p className="text-sm mb-2">
        Tracks the percentage of collected cash that has been handed over.
      </p>
      <ul className="list-disc pl-4 text-sm space-y-1">
        <li>Higher values show better accountability</li>
        <li>Critical for financial transparency</li>
        <li>Target: 95%+ settlement rate</li>
      </ul>
    </div>
  ),
  time: (
    <div>
      <p className="font-bold mb-2 text-base">⏱️ On-Time Performance</p>
      <p className="text-sm mb-2">
        Percentage of deliveries completed within the promised time window.
      </p>
      <ul className="list-disc pl-4 text-sm space-y-1">
        <li>Directly impacts customer satisfaction</li>
        <li>Industry benchmark: 85%+</li>
        <li>Factors: traffic, route planning, workload</li>
      </ul>
    </div>
  ),
};
